import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Upload, X } from 'lucide-react';
import type { InsertProduct } from '@shared/schema';

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (product: InsertProduct) => void;
}

export function AddProductModal({ isOpen, onClose, onSave }: AddProductModalProps) {
  const [formData, setFormData] = useState<InsertProduct>({
    category: 'groceries',
    description: '',
    chineseName: null,
    detailedDescription: null,
    size: '',
    retailPrice: 0,
    ourPrice: 0,
    imageUrl: null,
    active: true,
  });

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid image file (JPEG, PNG, or WebP)');
        return;
      }
      
      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('Image file is too large. Maximum size is 5MB.');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = 512;
          canvas.height = 512;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            console.error('Failed to get 2D context');
            return;
          }
          
          // Calculate dimensions to maintain aspect ratio
          const aspectRatio = img.width / img.height;
          let drawWidth = 512;
          let drawHeight = 512;
          let offsetX = 0;
          let offsetY = 0;
          
          if (aspectRatio > 1) {
            drawHeight = 512 / aspectRatio;
            offsetY = (512 - drawHeight) / 2;
          } else {
            drawWidth = 512 * aspectRatio;
            offsetX = (512 - drawWidth) / 2;
          }
          
          // Fill background
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, 512, 512);
          
          // Draw image centered and scaled
          ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
          
          // Convert to data URL with JPEG compression
          const resizedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
          setFormData({ ...formData, imageUrl: resizedDataUrl });
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const finalData = {
      ...formData,
      retailPrice: parseFloat(formData.retailPrice.toString()),
      ourPrice: parseFloat(formData.ourPrice.toString())
    };
    
    onSave(finalData);
    
    // Reset form
    setFormData({
      category: 'groceries',
      description: '',
      chineseName: null,
      detailedDescription: null,
      size: '',
      retailPrice: 0,
      ourPrice: 0,
      imageUrl: null,
      active: true,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Product</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="category">Category</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="produce">Produce</SelectItem>
                  <SelectItem value="meat-beef">Meat - Beef</SelectItem>
                  <SelectItem value="meat-chicken">Meat - Chicken</SelectItem>
                  <SelectItem value="meat-pork">Meat - Pork</SelectItem>
                  <SelectItem value="meat-hotpot">Meat - Hot Pot</SelectItem>
                  <SelectItem value="seafood">Seafood</SelectItem>
                  <SelectItem value="groceries">Groceries</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="description">Product Name</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g., Fresh Bananas"
              required
            />
          </div>

          <div>
            <Label htmlFor="chineseName">Chinese Name (中文名称)</Label>
            <Input
              id="chineseName"
              value={formData.chineseName || ''}
              onChange={(e) => setFormData({ ...formData, chineseName: e.target.value || null })}
              placeholder="输入中文名称"
            />
          </div>

          <div>
            <Label htmlFor="size">Size/Unit</Label>
            <Input
              id="size"
              value={formData.size}
              onChange={(e) => setFormData({ ...formData, size: e.target.value })}
              placeholder="e.g., 1 kg, 500g"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="retailPrice">Retail Price</Label>
              <Input
                id="retailPrice"
                type="number"
                step="0.01"
                value={formData.retailPrice || ''}
                onChange={(e) => setFormData({ ...formData, retailPrice: parseFloat(e.target.value) || 0 })}
                required
              />
            </div>

            <div>
              <Label htmlFor="ourPrice">Our Price</Label>
              <Input
                id="ourPrice"
                type="number"
                step="0.01"
                value={formData.ourPrice || ''}
                onChange={(e) => setFormData({ ...formData, ourPrice: parseFloat(e.target.value) || 0 })}
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="detailedDescription">Detailed Description (Optional)</Label>
            <Textarea
              id="detailedDescription"
              value={formData.detailedDescription || ''}
              onChange={(e) => setFormData({ ...formData, detailedDescription: e.target.value || null })}
              placeholder="Enter detailed product description (supports Markdown or HTML)"
              className="min-h-[100px] text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">Supports Markdown or HTML formatting</p>
          </div>

          <div>
            <Label htmlFor="active">Active Status</Label>
            <div className="flex items-center space-x-2 mt-2">
              <Switch
                id="active"
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
              <Label htmlFor="active">Product is active</Label>
            </div>
          </div>

          <div>
            <Label>Product Image</Label>
            <div className="mt-2">
              {formData.imageUrl ? (
                <div className="space-y-2">
                  <div className="relative inline-block">
                    <img 
                      src={formData.imageUrl} 
                      alt="Product preview" 
                      className="w-32 h-32 object-cover rounded border"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, imageUrl: null })}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    {formData.imageUrl.startsWith('data:') ? 'Uploaded image' : formData.imageUrl}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600">Choose one option:</div>
                  
                  <div className="border rounded-lg p-3 space-y-2">
                    <div className="font-medium text-sm">Option 1: Upload Image</div>
                    <label className="w-full h-24 bg-gray-100 border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 hover:border-gray-400 transition-colors">
                      <Upload className="h-6 w-6 text-gray-400" />
                      <span className="text-xs text-gray-500 mt-1">Click to upload (max 5MB)</span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                  
                  <div className="border rounded-lg p-3 space-y-2">
                    <div className="font-medium text-sm">Option 2: Image URL</div>
                    <Input
                      type="text"
                      value=""
                      onChange={(e) => {
                        const url = e.target.value.trim();
                        if (url) {
                          setFormData({ ...formData, imageUrl: url });
                        }
                      }}
                      placeholder="/images/products/filename.jpg"
                      className="text-sm"
                    />
                    <p className="text-xs text-gray-500">
                      Use existing images from /images/products/
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              Add Product
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}